import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Award, Download, Share2, ShieldCheck, AlertTriangle,
  CheckCircle, XCircle, Copy, ExternalLink, Mail, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { KPICard } from "@/components/KPICard";
import { getFinalRecommendation, createShareLink, shareViaEmail, downloadPdfUrl, fullReportPdfUrl } from "@/lib/api";

const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 });

export default function FinalRecommendation({ refreshKey, analysisId }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareLinkId, setShareLinkId] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFinalRecommendation(analysisId);
      setRec(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [analysisId]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await createShareLink({ title: "Loan Analysis Report" }, analysisId);
      const linkId = res.data.link_id;
      const url = `${window.location.origin}/shared/${linkId}`;
      setShareUrl(url);
      setShareLinkId(linkId);
    } catch (err) {
      toast.error("Failed to create share link");
    }
    setSharing(false);
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim()) { toast.error("Enter an email address"); return; }
    if (!shareLinkId) { toast.error("Generate a share link first"); return; }
    setSendingEmail(true);
    try {
      const res = await shareViaEmail({
        recipient_email: emailTo.trim(),
        subject: "Your Loan Analysis Report",
        share_link_id: shareUrl,
      });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(`Report emailed to ${emailTo}`);
        setEmailTo("");
      }
    } catch (err) {
      toast.error("Failed to send email");
    }
    setSendingEmail(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
  };

  const handlePrint = () => {
    window.open(fullReportPdfUrl(analysisId), "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!rec || !rec.best_offer) {
    return (
      <div className="bg-white border border-[#E7E5DF] rounded-md p-12 text-center" data-testid="recommendation-empty">
        <Award className="w-12 h-12 text-[#8A9A86] mx-auto mb-4" />
        <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-2">
          No Recommendation Available
        </h3>
        <p className="text-[#57534E] text-sm">
          Upload bank statements and add loan offers to generate a recommendation.
        </p>
      </div>
    );
  }

  const { best_offer, good_fits, borderline, not_supportable, concerns, max_safe_payment, summary, cash_flow_summary: cfs } = rec;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recommendation-section">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-semibold text-[#1C1917]">
            Final Recommendation
          </h2>
          <p className="text-sm text-[#57534E] mt-1">
            AI-powered loan analysis summary
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <Button
            data-testid="download-recommendation-btn"
            onClick={handlePrint}
            variant="outline"
            className="border-[#E7E5DF] hover:bg-[#F1EFEA]"
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                data-testid="share-btn"
                className="bg-[#1A362D] hover:bg-[#264B40]"
                onClick={() => { if (!shareUrl) handleShare(); }}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share with Borrower
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display']">Share Analysis</DialogTitle>
                <DialogDescription>Send this report to the borrower for review.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <p className="text-sm text-[#57534E]">
                  Share this link with the borrower so they can review the analysis and make an informed decision.
                </p>
                {sharing ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-6 h-6 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : shareUrl ? (
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input data-testid="share-url-input" value={shareUrl} readOnly className="font-['IBM_Plex_Mono'] text-sm" />
                      <Button data-testid="copy-link-btn" onClick={copyLink} variant="outline" size="icon" className="flex-shrink-0">
                        <Copy className="w-4 h-4" />
                      </Button>
                      <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="icon" className="flex-shrink-0">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                    <div className="border-t border-[#E7E5DF] pt-4">
                      <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Mail className="w-4 h-4" /> Email to Borrower
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          data-testid="email-to-input"
                          type="email"
                          placeholder="borrower@example.com"
                          value={emailTo}
                          onChange={(e) => setEmailTo(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleSendEmail(); }}
                        />
                        <Button
                          data-testid="send-email-btn"
                          onClick={handleSendEmail}
                          disabled={sendingEmail}
                          className="bg-[#1A362D] hover:bg-[#264B40] flex-shrink-0"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          {sendingEmail ? "Sending..." : "Send"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button onClick={handleShare} className="w-full bg-[#1A362D] hover:bg-[#264B40]">
                    Generate Share Link
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white border border-[#E7E5DF] rounded-md p-6 sm:p-8">
        <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-4">
          Analysis Summary
        </h3>
        <p className="text-[#57534E] leading-relaxed">{summary}</p>
      </div>

      {/* Best Offer Highlight */}
      <div className={`border-2 rounded-md p-6 sm:p-8 ${
        best_offer.recommendation === "Good Fit" ? "border-[#2D4A3E] bg-[#2D4A3E]/5" :
        best_offer.recommendation === "Borderline" ? "border-[#E3B505] bg-[#E3B505]/5" :
        "border-[#E07A5F] bg-[#E07A5F]/5"
      }`}>
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-6 h-6 text-[#1A362D]" />
          <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1C1917]">
            Recommended: {best_offer.lender_name}
          </h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-[#78716C] text-xs uppercase tracking-wider font-bold">Product</p>
            <p className="font-semibold text-[#1C1917] mt-1">{best_offer.product_type}</p>
          </div>
          <div>
            <p className="text-[#78716C] text-xs uppercase tracking-wider font-bold">Monthly Payment</p>
            <p className="font-semibold font-['IBM_Plex_Mono'] text-[#1C1917] mt-1">{fmtMoney(best_offer.monthly_payment)}</p>
          </div>
          <div>
            <p className="text-[#78716C] text-xs uppercase tracking-wider font-bold">New DSCR</p>
            <p className={`font-semibold font-['IBM_Plex_Mono'] mt-1 ${
              best_offer.new_dscr >= 1.5 ? "text-[#2D4A3E]" : best_offer.new_dscr >= 1.15 ? "text-[#E3B505]" : "text-[#E07A5F]"
            }`}>
              {best_offer.new_dscr >= 99 ? "N/A" : `${best_offer.new_dscr}x`}
            </p>
          </div>
          <div>
            <p className="text-[#78716C] text-xs uppercase tracking-wider font-bold">Total Cost</p>
            <p className="font-semibold font-['IBM_Plex_Mono'] text-[#E07A5F] mt-1">{fmtMoney(best_offer.total_cost)}</p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPICard
          title="Max Safe Payment"
          value={fmtMoney(max_safe_payment)}
          subtitle="80% of free cash flow"
          icon={ShieldCheck}
          variant="success"
        />
        <KPICard
          title="Good Fit Offers"
          value={`${good_fits?.length || 0} / ${(good_fits?.length || 0) + (borderline?.length || 0) + (not_supportable?.length || 0)}`}
          subtitle={borderline?.length ? `${borderline.length} borderline` : "All clear"}
          variant={good_fits?.length > 0 ? "success" : "warning"}
        />
        <KPICard
          title="Not Supportable"
          value={not_supportable?.length || 0}
          subtitle={not_supportable?.length ? "Too aggressive" : "None"}
          variant={not_supportable?.length ? "danger" : "success"}
        />
      </div>

      {/* Offer Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {good_fits?.length > 0 && (
          <div className="bg-white border border-[#2D4A3E]/20 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-[#2D4A3E]" />
              <h4 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E]">Good Fit</h4>
            </div>
            <ul className="space-y-1.5">
              {good_fits.map((name, i) => (
                <li key={i} className="text-sm text-[#57534E] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2D4A3E]" />{name}
                </li>
              ))}
            </ul>
          </div>
        )}
        {borderline?.length > 0 && (
          <div className="bg-white border border-[#E3B505]/20 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#E3B505]" />
              <h4 className="font-['Playfair_Display'] font-semibold text-[#E3B505]">Borderline</h4>
            </div>
            <ul className="space-y-1.5">
              {borderline.map((name, i) => (
                <li key={i} className="text-sm text-[#57534E] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E3B505]" />{name}
                </li>
              ))}
            </ul>
          </div>
        )}
        {not_supportable?.length > 0 && (
          <div className="bg-white border border-[#E07A5F]/20 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="w-5 h-5 text-[#E07A5F]" />
              <h4 className="font-['Playfair_Display'] font-semibold text-[#E07A5F]">Not Supportable</h4>
            </div>
            <ul className="space-y-1.5">
              {not_supportable.map((name, i) => (
                <li key={i} className="text-sm text-[#57534E] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F]" />{name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Concerns */}
      {concerns?.length > 0 && (
        <div className="bg-white border border-[#E07A5F]/30 rounded-md p-6" data-testid="concerns-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#E07A5F]" />
            <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#E07A5F]">
              Concerns &amp; Red Flags
            </h3>
          </div>
          <ul className="space-y-2">
            {concerns.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#57534E]">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E07A5F] flex-shrink-0" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
